import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Notifications,
  Settings,
  Logout,
  Dashboard,
  Assignment,
  Add,
  Support,
  Close,
  Home,
  Person,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearAuthCookies } from '../utils/api';
import RowadLogo from './RowadLogo';
import EnhancedNewTicketDialog from './Tickets/EnhancedNewTicket';

const EnhancedHeader = ({ onTicketCreated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showNewTicketDialog, setShowNewTicketDialog] = useState(false);
  
  // Mock user data - replace with actual user data
  const userData = {
    name: 'User',
    email: 'user@rowad-rme.com',
    avatar: null,
    unreadNotifications: 3,
  };

  // Navigation items with icons and descriptions
  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/enhanced-tickets',
      icon: <Dashboard />,
      description: 'View your tickets',
      isActive: location.pathname === '/enhanced-tickets',
    },
    {
      label: 'My Account',
      path: '/my-account',
      icon: <Person />,
      description: 'Manage profile settings',
      isActive: location.pathname === '/my-account',
    },
  ];

  // Mock notifications - replace with actual notification data
  const notifications = [
    {
      id: 1,
      title: 'Ticket #12345 Updated',
      message: 'Your IT support ticket has been resolved',
      time: '5 minutes ago',
      unread: true,
    },
    {
      id: 2,
      title: 'New System Maintenance',
      message: 'Scheduled maintenance on Sunday 2 AM - 4 AM',
      time: '2 hours ago',
      unread: true,
    },
    {
      id: 3,
      title: 'Ticket #12340 Assigned',
      message: 'Your request has been assigned to John Smith',
      time: '1 day ago',
      unread: false,
    },
  ];

  const handleOpenNotifications = (event) => {
    setAnchorElNotifications(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setAnchorElNotifications(null);
  };

  const handleLogout = () => {
    clearAuthCookies();
    navigate('/sign-in', { replace: true });
  };

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Mobile Drawer Content
  const drawerContent = (
    <Box sx={{ width: 280, height: '100%', bgcolor: 'background.default' }} >
      <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <RowadLogo />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
          Service Desk
        </Typography>
      </Box>
      
      <List sx={{ pt: 0 }}>
        {navigationItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              selected={item.isActive}
              onClick={() => {
                navigate(item.path);
                setMobileDrawerOpen(false);
              }}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'grey.200',
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'grey.300',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: item.isActive ? 'text.primary' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                secondary={item.description}
              />
            </ListItemButton>
          </ListItem>
        ))}
        
        <Divider sx={{ my: 2 }} />
        
        {/* Create New Ticket in Mobile Menu */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              setShowNewTicketDialog(true);
              setMobileDrawerOpen(false);
            }}
            sx={{
              bgcolor: '#B41736',
              color: 'white',
              '&:hover': {
                bgcolor: '#8f1129',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white' }}>
              <Add />
            </ListItemIcon>
            <ListItemText 
              primary="Create New Ticket" 
              secondary="Submit a support request"
              secondaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
            />
          </ListItemButton>
        </ListItem>
        
        <Divider sx={{ my: 2 }} />
        
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => {
              navigate('/my-account');
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <ListItemText primary="My Account" />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout} 
            sx={{ 
              color: 'error.main',
              bgcolor: 'error.lighter',
              '&:hover': {
                bgcolor: 'error.light',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'error.main' }}>
              <Logout />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={2}
        sx={{ 
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: 64 }, py: 0.5 }}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                size="large"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: isMobile ? 1 : 0 }}>
              <RowadLogo />
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', ml: 4 }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => navigate(item.path)}
                    startIcon={item.icon}
                    sx={{
                      mx: 1,
                      color: item.isActive ? 'text.primary' : 'text.secondary',
                      bgcolor: item.isActive ? 'grey.200' : 'transparent',
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      '&:hover': {
                        bgcolor: item.isActive ? 'grey.300' : 'action.hover',
                      },
                      fontWeight: item.isActive ? 600 : 400,
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Right Side Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Create New Ticket Button */}
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowNewTicketDialog(true)}
                sx={{
                  background: '#B41736',
                  color: 'white',
                  borderRadius: 2,
                  px: 4,
                  py: 1,
                  minWidth: 180,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  boxShadow: '0 4px 12px rgba(180, 23, 54, 0.4)',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: '#8f1129',
                    boxShadow: '0 6px 16px rgba(180, 23, 54, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                New Ticket
              </Button>

              {/* Mobile Create Ticket Button */}
              <Tooltip title="Create New Ticket">
                <IconButton
                  onClick={() => setShowNewTicketDialog(true)}
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    bgcolor: '#B41736',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#8f1129',
                    },
                  }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
              {/* Notifications */}
              {/* <Tooltip title="Notifications">
                <IconButton
                  size="large"
                  onClick={handleOpenNotifications}
                  sx={{ color: 'text.primary' }}
                >
                  <Badge badgeContent={userData.unreadNotifications} color="error">
                    <Notifications />
                  </Badge>
                </IconButton>
              </Tooltip> */}

              {/* Logout Icon */}
              <Tooltip title="Logout">
                <IconButton 
                  onClick={handleLogout} 
                  sx={{ 
                    p: 0.5,
                    width: '46px',
                    marginLeft: '54px',
                    color: 'error.main',
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: 'error.dark',
                    },
                  }}
                >
                  <Logout />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={handleDrawerToggle}>
            <Close />
          </IconButton>
        </Box>
        {drawerContent}
      </Drawer>

      {/* Notifications Menu */}
      <Menu
        sx={{ mt: '45px' }}
        anchorEl={anchorElNotifications}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElNotifications)}
        onClose={handleCloseNotifications}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 400,
            mt: 1,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have {userData.unreadNotifications} unread notifications
          </Typography>
        </Box>
        
        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {notifications.map((notification) => (
            <MenuItem 
              key={notification.id} 
              sx={{ 
                py: 2, 
                borderBottom: '1px solid', 
                borderColor: 'divider',
                alignItems: 'flex-start',
                whiteSpace: 'normal',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {notification.title}
                  </Typography>
                  {notification.unread && (
                    <Chip 
                      label="New" 
                      size="small" 
                      color="primary" 
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.time}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Box>
        
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button fullWidth variant="outlined" size="small">
            View All Notifications
          </Button>
        </Box>
      </Menu>

      {/* Enhanced New Ticket Dialog */}
      <EnhancedNewTicketDialog
        open={showNewTicketDialog}
        onClose={() => setShowNewTicketDialog(false)}
        onTicketCreated={() => {
          setShowNewTicketDialog(false);
          
          // If a refresh callback was provided (from ticket table), call it
          if (onTicketCreated) {
            onTicketCreated();
          } else if (location.pathname !== '/enhanced-tickets' && location.pathname !== '/') {
            // If not on the tickets page, navigate there to see the new ticket
            navigate('/enhanced-tickets');
          }
          // If already on tickets page and no callback, the table will show the new ticket on next load
        }}
      />
    </>
  );
};

export default EnhancedHeader;
